import { Module } from "../../../lib/ui/Module.js";
import { ObjectInspector } from "../../../lib/ui/controls/data/ObjectInspector.js";
import { CollectionObjectTreeNodeFactory, ObjectTree, StaticObjectTreeNodeFactory, ValueObjectTreeNodeFactory } from "../../../lib/ui/controls/data/ObjectTree.js";
import { ModelSectionJsonDataManager } from "../../domain/json/ModelSectionJsonDataManager.js";
//
import { GeoSectionModel } from "../geo-sections/models/GeoSectionModel.js";
import { GeoParamsModel } from "../geo-sections/models/GeoParamsModel.js";
import { GeoTypeModel } from "../geo-sections/models/GeoTypeModel.js";
import { GeoLayerModel } from "../geo-sections/models/GeoLayerModel.js";
import { WaterBodyModel } from "../geo-sections/models/WaterBodyModel.js";
//
import { GeoBoundaryModel } from "../model-sections/models/GeoBoundaryModel.js";
import { GeoLoadModel } from "../model-sections/models/GeoLoadModel.js";
import { GeoObjectModel } from "../model-sections/models/GeoObjectModel.js";
import { ModelIntervalModel } from "../model-sections/models/ModelIntervalModel.js";
import { ModelSectionModel } from "../model-sections/models/ModelSectionModel.js";
//
import { EarthPressuresCalculationModel } from "../earth-pressures/models/EarthPressuresCalculationModel.js";
import { EarthPressuresModelIntervalModel } from "../earth-pressures/models/EarthPressuresModelIntervalModel.js";
import { EarthPressuresDataModel } from "../earth-pressures/models/EarthPressuresDataModel.js";
//
import { FacePressuresCalculationModel } from "../face-pressures/models/FacePressuresCalculationModel.js";
import { FacePressuresModelIntervalModel } from "../face-pressures/models/FacePressuresModelIntervalModel.js";
import { FacePressuresForcesDataModel } from "../face-pressures/models/FacePressuresForcesDataModel.js";
import { FacePressuresGeotechDataModel } from "../face-pressures/models/FacePressuresGeotechDataModel.js";
import { FacePressuresForcesAtAngleModel } from "../face-pressures/models/FacePressuresForcesAtAngleModel.js";
import { FacePressuresPressuresAtLevelModel } from "../face-pressures/models/FacePressuresPressuresAtLevelModel.js";

function flattenTreeNodes(nodes = []) {

    return nodes.flatMap((node) => [node, ...flattenTreeNodes(node.children || [])]);

}

export class ModelSectionEditor extends Module {

    constructor(parent, options = {}) {

        options = Object.assign({
            moduleName: 'ModelSectionEditor',
            moduleTitle: 'Model Section Editor',
        }, options ?? {});

        super(parent, options);

        this.workspaceManager = null;
        this.modelSection = null;
        this.modelSectionData = null;
        this.loadError = null;
        this.isLoading = false;

        this.geoSectionModel = null;
        this.geoParamsModel = null;
        this.geoTypeModel = null;
        this.geoLayerModel = null;
        this.waterBodyModel = null;
        this.geoBoundaryModel = null;
        this.geoLoadModel = null;
        this.geoObjectModel = null;
        this.modelIntervalModel = null;
        this.modelSectionModel = null;

        this.earthPressuresCalculationModel = null;
        this.earthPressuresModelIntervalModel = null;
        this.earthPressuresDataModel = null;

        this.facePressuresCalculationModel = null;
        this.facePressuresModelIntervalModel = null;
        this.facePressuresForcesDataModel = null;
        this.facePressuresGeotechDataModel = null;
        this.facePressuresForcesAtAngleModel = null;
        this.facePressuresPressuresAtLevelModel = null;

        this.selectedTreeNode = null;
        this.selectedTreeNodeKey = null;
        this.objectTree = null;
        this.objectInspector = null;

    }

    getWorkspaceManager() {

        if (!this.workspaceManager)
            this.workspaceManager = this.getServices().getWorkspaceManager();

        return this.workspaceManager;

    }

    getRouteParams(activeRoute = null) {

        return activeRoute?.params || this.getApp()?.getActiveRouteParams() || {};

    }

    async _prepare(options, activeRoute) {

        await super._prepare(options, activeRoute);

        const authManger = this.getServices().getAuthManager();

        this.geoSectionModel = new GeoSectionModel(authManger);
        this.geoParamsModel = new GeoParamsModel(authManger);
        this.geoTypeModel = new GeoTypeModel(authManger);
        this.geoLayerModel = new GeoLayerModel(authManger);
        this.waterBodyModel = new WaterBodyModel(authManger);
        this.geoBoundaryModel = new GeoBoundaryModel(authManger);
        this.geoLoadModel = new GeoLoadModel(authManger);
        this.geoObjectModel = new GeoObjectModel(authManger);
        this.modelIntervalModel = new ModelIntervalModel(authManger);
        this.modelSectionModel = new ModelSectionModel(authManger);

        this.earthPressuresCalculationModel = new EarthPressuresCalculationModel(authManger);
        this.earthPressuresModelIntervalModel = new EarthPressuresModelIntervalModel(authManger);
        this.earthPressuresDataModel = new EarthPressuresDataModel(authManger);

        this.facePressuresCalculationModel = new FacePressuresCalculationModel(authManger);
        this.facePressuresModelIntervalModel = new FacePressuresModelIntervalModel(authManger);
        this.facePressuresForcesDataModel = new FacePressuresForcesDataModel(authManger);
        this.facePressuresGeotechDataModel = new FacePressuresGeotechDataModel(authManger);
        this.facePressuresForcesAtAngleModel = new FacePressuresForcesAtAngleModel(authManger);
        this.facePressuresPressuresAtLevelModel = new FacePressuresPressuresAtLevelModel(authManger);

        this.objectTree = new ObjectTree(this, {
            css: 'model-section-editor__object-tree',
            rootFactory: () => this.createModelSectionTreeFactory(),
            context: () => ({ editor: this, modelSection: this.modelSectionData }),
            selectedKey: () => this.selectedTreeNodeKey,
            onNodeSelect: (node) => {
                this.selectedTreeNode = node;
                this.selectedTreeNodeKey = node.key;
                this.invalidate();
            },
        });

        this.objectInspector = new ObjectInspector(this, {
            css: 'model-section-editor__object-inspector large-width round padding border full-height',
            model: () => this.getSelectedInspectorTarget()?.model ?? null,
            value: () => this.getSelectedInspectorTarget()?.value ?? null,
            pinnedFieldNames: ['id', 'uid', 'name'],
            emptyText: 'Select an inspectable object in the tree.',
            onFieldValueChange: (value, field, data) => {
                if (!field || !data)
                    return false;

                field.setValue(data, value);
                data.$changed = true;
                if (this.modelSectionData)
                    this.modelSectionData.$changed = true;
                this.invalidate();
                return true;
            },
        });

        await Promise.all([
            this.geoSectionModel,
            this.geoParamsModel,
            this.geoTypeModel,
            this.geoLayerModel,
            this.waterBodyModel,
            this.geoBoundaryModel,
            this.geoLoadModel,
            this.geoObjectModel,
            this.modelIntervalModel,
            this.modelSectionModel,
            this.earthPressuresCalculationModel,
            this.earthPressuresModelIntervalModel,
            this.earthPressuresDataModel,
            this.facePressuresCalculationModel,
            this.facePressuresModelIntervalModel,
            this.facePressuresForcesDataModel,
            this.facePressuresGeotechDataModel,
            this.facePressuresForcesAtAngleModel,
            this.facePressuresPressuresAtLevelModel,
        ].map((model) => this.getApp().prepareDataModel(model)));

    }

    async _refresh(options, activeRoute) {

        await super._refresh(options, activeRoute);

        const { workspaceUid, projectUid, modelSectionUid } = this.getRouteParams(activeRoute);
        await this.loadModelSection(workspaceUid, projectUid, modelSectionUid);

    }

    async loadModelSection(workspaceUid, projectUid, modelSectionUid) {

        if (!workspaceUid || !projectUid || !modelSectionUid)
            throw new Error('Workspace UID, project UID and model section UID are required.');

        this.isLoading = true;
        this.loadError = null;

        try {
            const modelSection = await this.getWorkspaceManager().getWorkspaceProjectModelSection(
                workspaceUid,
                projectUid,
                modelSectionUid
            );

            if (!modelSection)
                throw new Error('Model section neexistuje nebo k ní nemáte oprávnění.');

            this.modelSection = modelSection;
            this.modelSectionData = this.parseModelSectionData(modelSection.data, modelSection);
            this.selectInitialTreeNode();
        } catch (error) {
            this.modelSection = null;
            this.modelSectionData = null;
            this.selectedTreeNode = null;
            this.selectedTreeNodeKey = null;
            this.loadError = error;
        } finally {
            this.isLoading = false;
        }

    }

    selectInitialTreeNode() {

        const nodes = this.objectTree?.getNodes?.() ?? [];
        const flattenedNodes = flattenTreeNodes(nodes);
        const selectedNode = flattenedNodes.find((node) => node.key == this.selectedTreeNodeKey)
            || flattenedNodes.find((node) => node.isInspectable())
            || flattenedNodes[0]
            || null;

        this.selectedTreeNode = selectedNode;
        this.selectedTreeNodeKey = selectedNode?.key ?? null;

    }

    parseModelSectionData(data, modelSection = null) {

        try {
            return ModelSectionJsonDataManager.deserializeModelSection(data, modelSection);
        } catch (error) {
            if (error instanceof SyntaxError)
                throw new Error('Data model section nejsou platný JSON.');
            throw error;
        }

    }

    getSelectedInspectorTarget() {

        return this.selectedTreeNode?.inspectorTarget ?? null;

    }

    createModelSectionTreeFactory() {

        return new StaticObjectTreeNodeFactory({
            key: 'model-section',
            title: () => this.modelSectionData?.name || this.modelSection?.name || 'Model Section',
            value: () => this.modelSectionData,
            inspectorTarget: () => this.modelSectionData ? { model: this.modelSectionModel, value: this.modelSectionData } : null,
            expanded: true,
            children: [
                new StaticObjectTreeNodeFactory({
                    key: 'geo-section',
                    title: 'Geo Section',
                    value: () => this.modelSectionData?.geoSection ?? null,
                    inspectorTarget: () => this.modelSectionData?.geoSection ? { model: this.geoSectionModel, value: this.modelSectionData.geoSection } : null,
                    children: [
                        this.createObjectNodeFactory('geo-params', 'Params', () => this.modelSectionData?.geoSection?.geoParams ?? null, () => this.geoParamsModel),
                        this.createCollectionNodeFactory('geo-types', 'Geotypes', () => this.modelSectionData?.geoSection?.geoTypes ?? [], () => this.geoTypeModel),
                        this.createCollectionNodeFactory('layers', 'Layers', () => this.modelSectionData?.geoSection?.layers ?? [], () => this.geoLayerModel),
                        this.createCollectionNodeFactory('water-bodies', 'Water Bodies', () => this.modelSectionData?.geoSection?.waterBodies ?? [], () => this.waterBodyModel),
                    ],
                }),
                this.createCollectionNodeFactory('intervals', 'Intervals', () => this.modelSectionData?.intervals ?? [], () => this.modelIntervalModel),
                this.createCollectionNodeFactory('boundaries', 'Boundaries', () => this.modelSectionData?.boundaries ?? [], () => this.geoBoundaryModel),
                this.createCollectionNodeFactory('loads', 'Loads', () => this.modelSectionData?.loads ?? [], () => this.geoLoadModel),
                this.createCollectionNodeFactory('objects', 'Objects', () => this.modelSectionData?.objects ?? [], () => this.geoObjectModel),
                new StaticObjectTreeNodeFactory({
                    key: 'earth-pressures',
                    title: 'Earth Pressures',
                    value: () => this.modelSectionData?.earthPressuresCalculation ?? null,
                    inspectorTarget: () => this.modelSectionData?.earthPressuresCalculation ? { model: this.earthPressuresCalculationModel, value: this.modelSectionData.earthPressuresCalculation } : null,
                    children: [
                        this.createCollectionNodeFactory('intervals', 'Intervals', () => this.modelSectionData?.earthPressuresCalculation?.intervals ?? [], () => this.earthPressuresModelIntervalModel),
                        this.createCollectionNodeFactory('pressures', 'Pressures', () => this.modelSectionData?.earthPressuresCalculation?.pressures ?? [], () => this.earthPressuresDataModel),
                    ],
                }),
                new StaticObjectTreeNodeFactory({
                    key: 'face-pressures',
                    title: 'Face Pressures',
                    value: () => this.modelSectionData?.facePressuresCalculation ?? null,
                    inspectorTarget: () => this.modelSectionData?.facePressuresCalculation ? { model: this.facePressuresCalculationModel, value: this.modelSectionData.facePressuresCalculation } : null,
                    children: [
                        this.createCollectionNodeFactory('intervals', 'Intervals', () => this.modelSectionData?.facePressuresCalculation?.intervals ?? [], () => this.facePressuresModelIntervalModel),
                        this.createObjectNodeFactory('geotech-data', 'Geotech Data', () => this.modelSectionData?.facePressuresCalculation?.geotechData ?? null, () => this.facePressuresGeotechDataModel),
                        this.createObjectNodeFactory('forces-data', 'Forces Data', () => this.modelSectionData?.facePressuresCalculation?.forcesData ?? null, () => this.facePressuresForcesDataModel),
                        this.createCollectionNodeFactory('forces-at-angles', 'Forces At Angles', () => this.modelSectionData?.facePressuresCalculation?.forcesAtAngles ?? [], () => this.facePressuresForcesAtAngleModel),
                        this.createCollectionNodeFactory('pressures-at-levels', 'Pressures At Levels', () => this.modelSectionData?.facePressuresCalculation?.pressuresAtLevels ?? [], () => this.facePressuresPressuresAtLevelModel),
                    ],
                }),
            ],
        });

    }

    createObjectNodeFactory(key, title, getValue, getModel) {

        return new ValueObjectTreeNodeFactory({
            getKey: () => key,
            getTitle: () => title,
            getValue: () => getValue(),
            getInspectorTarget: (value) => value ? { model: getModel(), value } : null,
        });

    }

    createCollectionNodeFactory(key, title, getItems, getModel) {

        return new CollectionObjectTreeNodeFactory({
            key,
            title: () => `${title} (${(getItems() || []).length})`,
            items: () => getItems() || [],
            itemFactory: new ValueObjectTreeNodeFactory({
                getKey: (value, context) => value?.uid || value?.id || context.index,
                getTitle: (value, context) => value?.name || value?.uid || value?.id || `${title} ${context.index + 1}`,
                getInspectorTarget: (value) => value ? { model: getModel(), value } : null,
            }),
        });

    }

    _render(r, vm) {

        const editor = this;
        const title = editor.modelSection?.name || 'Model Section Editor';

        return r.module(null, [
            r.header(null, [
                r.nav(null, [
                    r.iconButton('#back', { css: 'transparent circle', props: { type: 'button', onclick: () => editor.getApp().navigateBack() } }),
                    r.h4([title]),
                ]),
            ]),
            r.section({ css: 'padding no-margin no-scroll' }, [
                r.stack({ css: 'full-height' },
                    [renderContent()].flat(),
                ),
            ]),
        ]);

        function renderContent() {

            if (editor.isLoading)
                return r.p(['Načítám model section...']);

            if (editor.loadError)
                return r.p('error', [editor.getLoadErrorMessage(editor.loadError)]);

            if (!editor.modelSection)
                return r.p(['Model section není načtená.']);

            //return renderModelSectionTreeEditor();

            return [
                r.div({ css: 'model-section-editor__tree-pane surface-container-lowest round right-margin scroll-y' }, [
                    editor.objectTree
                        ? editor.objectTree.render(r, vm)
                        : r.empty({ type: 'error', title: 'Object tree is not available.' }),
                ]),
                r.div({ css: 'model-section-editor__inspector-pane min' }, [
                    editor.getSelectedInspectorTarget() && editor.objectInspector
                        ? editor.objectInspector.render(r, vm)
                        : r.empty({ title: 'Select an inspectable object in the tree.' }),
                ]),
            ];

        }

        // function renderModelSectionTreeEditor() {

        //     return r.section({ css: 'surface-container-high round padding top-margin model-section-editor__tree-editor' }, [
        //         r.div({ css: 'model-section-editor__tree-pane' }, [
        //             editor.objectTree
        //                 ? editor.objectTree.render(r, vm)
        //                 : r.empty({ type: 'error', title: 'Object tree is not available.' }),
        //         ]),
        //         r.div({ css: 'model-section-editor__inspector-pane scroll-y max' }, [
        //             editor.getSelectedInspectorTarget() && editor.objectInspector
        //                 ? editor.objectInspector.render(r, vm)
        //                 : r.empty({ title: 'Select an inspectable object in the tree.' }),
        //         ]),
        //     ]);

        // }

    }

    getLoadErrorMessage(error) {

        const code = error?.details?.code;
        if (code === 403001 || code === 404100 || code === 404101)
            return 'Model section neexistuje nebo k ní nemáte oprávnění.';

        return error?.details?.message || error?.message || 'Model section se nepodařilo načíst.';

    }

}
